import * as React from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Badge from "@mui/material/Badge";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import MenuIcon from "@mui/icons-material/Menu";
import AccountCircle from "@mui/icons-material/AccountCircle";
import NotificationsIcon from "@mui/icons-material/Notifications";
import LogoutIcon from "@mui/icons-material/Logout";
import ShoppingCartCheckoutIcon from "@mui/icons-material/ShoppingCartCheckout";
import HeartBrokenIcon from "@mui/icons-material/HeartBroken";
import InventoryIcon from "@mui/icons-material/Inventory";
import MoreIcon from "@mui/icons-material/MoreVert";
import {
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Select,
} from "@mui/material";
import DangerousIcon from "@mui/icons-material/Dangerous";
import CancelIcon from "@mui/icons-material/Cancel";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ArrowBackIosRoundedIcon from "@mui/icons-material/ArrowBackIosRounded";
import DoubleArrowIcon from "@mui/icons-material/DoubleArrow";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { AccountModel } from "../../../domain/models";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import RuleIcon from "@mui/icons-material/Rule";
import PrintIcon from "@mui/icons-material/Print";

const drawerWidth = 200;

interface Props {
  title?: string;
  onBack?: () => void;
  window?: () => Window;
}

export default function PrimarySearchAppBar(props: Props) {
  const { window } = props;
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [open, setOpen] = React.useState(false);
  const [name, setName] = React.useState("");
  const [role, setRole] = React.useState("");
  const [warehouses, setWarehouses] = React.useState<any[]>([]);
  const [currentWarehouse, setCurrentWarehouseState] = React.useState<
    { id: number; name: string } | undefined
  >();
  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] =
    React.useState<null | HTMLElement>(null);

  const isMenuOpen = Boolean(anchorEl);
  const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);

  const getCurrentAccount = useSelector<RootState, () => AccountModel>(
    (state) => state.getCurrentAccount
  );
  const setCurrentAccount = useSelector<
    RootState,
    (account: AccountModel | undefined) => void
  >((state) => state.setCurrentAccount);

  const getCurrentWarehouse = useSelector<
    RootState,
    () => { id: number; name: string }
  >((state) => state.getCurrentWarehouse);

  const setCurrentWarehouse = useSelector<
    RootState,
    (warehouse: { id: number; name: string }) => void
  >((state) => state.setCurrentWarehouse);

  const container =
    window !== undefined ? () => window().document.body : undefined;

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMoreAnchorEl(null);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    handleMobileMenuClose();
  };

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMoreAnchorEl(event.currentTarget);
  };

  const menuId = "primary-search-account-menu";
  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      id={menuId}
      keepMounted
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      <MenuItem onClick={handleMenuClose}>Profile</MenuItem>
      <MenuItem onClick={handleMenuClose}>My account</MenuItem>
    </Menu>
  );

  const mobileMenuId = "primary-search-account-menu-mobile";
  const renderMobileMenu = (
    <Menu
      anchorEl={mobileMoreAnchorEl}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      id={mobileMenuId}
      keepMounted
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      open={isMobileMenuOpen}
      onClose={handleMobileMenuClose}
    >
      <MenuItem>
        <IconButton
          size="large"
          aria-label="show 17 new notifications"
          color="inherit"
        >
          <Badge badgeContent={17} color="error">
            <NotificationsIcon />
          </Badge>
        </IconButton>
        <p>Notifications</p>
      </MenuItem>
      <MenuItem onClick={handleProfileMenuOpen}>
        <IconButton
          size="large"
          aria-label="account of current user"
          aria-controls="primary-search-account-menu"
          aria-haspopup="true"
          color="inherit"
        >
          <AccountCircle />
        </IconButton>
        <p>Profile</p>
      </MenuItem>
    </Menu>
  );

  const navigate = useNavigate();

  const navItems = [
    {
      text: "Produtos",
      icon: <InventoryIcon fontSize="small" />,
      onClick: () => {
        navigate("/products");
      },
      whiteList: ["admin", "seller"],
    },
    {
      text: "Posições",
      icon: <LocationOnIcon fontSize="small" />,
      onClick: () => {
        navigate("/positions");
      },
      whiteList: ["admin", "seller"],
    },
    {
      text: "Entradas",
      icon: <DoubleArrowIcon fontSize="small" />,
      onClick: () => {
        navigate("/receipts");
      },
      whiteList: ["admin"],
    },
    {
      text: "Separar",
      icon: <ShoppingCartCheckoutIcon fontSize="small" />,
      onClick: () => {
        navigate("/separation");
      },
      whiteList: ["admin"],
    },
    {
      text: "Relatórios",
      icon: <InsertDriveFileIcon fontSize="small" />,
      onClick: () => {
        navigate("/reports");
      },
      whiteList: ["admin"],
    },
    {
      text: "Quebras",
      icon: <HeartBrokenIcon fontSize="small" />,
      onClick: () => {
        navigate("/breakage");
      },
      whiteList: ["admin"],
    },
    {
      text: "Auditoria",
      icon: <RuleIcon fontSize="small" />,
      onClick: () => {
        navigate("/audit");
      },
      whiteList: ["admin"],
    },
    {
      text: "Ajustes",
      icon: <DangerousIcon fontSize="small" />,
      onClick: () => {
        navigate("/adjustments");
      },
      whiteList: ["admin"],
    },
    {
      text: "Imprimir Etiquetas",
      icon: <PrintIcon fontSize="small" />,
      onClick: () => {
        navigate("/print");
      },
      whiteList: ["admin"],
    },
    {
      text: "Sair",
      icon: <LogoutIcon fontSize="small" />,
      onClick: () => {
        setCurrentAccount(undefined);
        localStorage.removeItem("currentWarehouse");
        navigate("/login");
      },
    },
  ];

  React.useEffect(() => {
    if (getCurrentAccount) {
      const account = getCurrentAccount();
      if (!account.warehouses) {
        return navigate("/login");
      }
      setRole(account.role?.name || "");
      setName(account.name);
      setWarehouses(account.warehouses);
      if (account?.warehouses?.length === 1) {
        setCurrentWarehouseState(account.warehouses[0]);
        return setCurrentWarehouse(account.warehouses[0]);
      }
    }
    if (getCurrentWarehouse) {
      const current = getCurrentWarehouse();
      setCurrentWarehouseState(current);
    }
  }, []);

  const drawer = (
    <>
      <Box onClick={handleDrawerToggle}>
        <List>
          <ListItem>
            <ListItemButton>
              <ListItemIcon>
                <CancelIcon />
              </ListItemIcon>
            </ListItemButton>
          </ListItem>
        </List>
      </Box>
      <Box onClick={handleDrawerToggle}>
        <List>
          {navItems.map((item) => {
            if (
              item.whiteList &&
              !item.whiteList?.includes("*") &&
              !item.whiteList?.includes(role)
            ) {
              return "";
            }
            return (
              <ListItem
                key={item.text}
                sx={{ height: "40px" }}
                onClick={item.onClick}
              >
                <ListItemButton sx={{ textAlign: "center", fontSize: "14px" }}>
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    sx={{
                      textAlign: "left",
                      "& .MuiTypography-root": { fontSize: "12px" },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>
    </>
  );

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar>
          <IconButton
            size="large"
            edge="start"
            color="inherit"
            aria-label="open drawer"
            sx={{ mr: 2 }}
            onClick={handleDrawerToggle}
          >
            <MenuIcon />
          </IconButton>
          <Divider
            orientation="vertical"
            variant="middle"
            flexItem
            sx={{ borderColor: "white" }}
          />
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={props.onBack}
            sx={{ px: "20px" }}
          >
            <ArrowBackIosRoundedIcon />
          </IconButton>
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{ display: { xs: "none", sm: "block" } }}
          >
            {props.title}
          </Typography>

          <Box sx={{ flexGrow: 1 }} />
          <Box
            sx={{ display: { xs: "none", md: "flex" }, alignItems: "center" }}
          >
            <Select
              value={currentWarehouse?.id || ""}
              label={"Unidade"}
              variant="standard"
              sx={{ color: "inherit", minWidth: "100px", marginRight: "20px" }}
              onChange={(e) => {
                const warehouse = warehouses.find(
                  (item) => item.id === e.target.value
                );

                setCurrentWarehouse(warehouse);
                setCurrentWarehouseState(warehouse);
              }}
            >
              {warehouses.map((warehouse) => (
                <MenuItem key={warehouse.id} value={warehouse.id}>
                  {warehouse.name}
                </MenuItem>
              ))}
            </Select>
            {/* <IconButton
              size="large"
              aria-label="show 17 new notifications"
              color="inherit"
              sx={{ px: "40px" }}
            >
              <Badge badgeContent={17} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton> */}
            <Typography>{`Olá ${name ? ", " + name : ""}`}</Typography>
            <IconButton
              size="large"
              edge="end"
              aria-label="account of current user"
              aria-controls={menuId}
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              color="inherit"
            >
              <AccountCircle />
            </IconButton>
          </Box>
          <Box sx={{ display: { xs: "flex", md: "none" } }}>
            <IconButton
              size="large"
              aria-label="show more"
              aria-controls={mobileMenuId}
              aria-haspopup="true"
              onClick={handleMobileMenuOpen}
              color="inherit"
            >
              <MoreIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          container={container}
          variant="temporary"
          open={open}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="temporary"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
          open={open}
          onClose={() => {
            setOpen(false);
          }}
        >
          {drawer}
        </Drawer>
      </Box>
      {renderMobileMenu}
      {renderMenu}
    </Box>
  );
}
